// File processing utilities for the chatbox
import { processPDFOnServer } from "./serverPdfProcessor.js"

// Simple text extraction for basic file types
const extractTextFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const content = e.target.result
        resolve({
          type: "text",
          content: content,
          name: file.name,
          originalFile: file,
        })
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => {
      reject(new Error("Failed to read file"))
    }

    reader.readAsText(file)
  })
}

// Process a single file based on its type
export const processFile = async (file) => {
  console.log("Processing file:", file.name, "Type:", file.type, "Size:", file.size)

  try {
    // Handle different file types
    if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
      console.log("Processing PDF file on server...")
      return await processPDFOnServer(file)
    } else if (file.type.startsWith("text/") || file.name.toLowerCase().match(/\.(txt|csv|md|json)$/)) {
      console.log("Processing text file...")
      return await extractTextFromFile(file)
    } else {
      // For unsupported file types, return basic info
      console.log("Unsupported file type, returning basic info")
      return {
        type: "unsupported",
        content: `[File attached: ${file.name}]\n\nFile type: ${file.type}\nSize: ${Math.round(file.size / 1024)} KB\n\nThis file type is not supported for text extraction, but it has been attached to your message.`,
        name: file.name,
        originalFile: file,
      }
    }
  } catch (error) {
    console.error("Error processing file:", error)

    return {
      type: "error",
      content: `[Error processing file: ${file.name}]\n\nError: ${error.message}\n\nThe file could not be processed, but it has been attached to your message.`,
      name: file.name,
      originalFile: file,
    }
  }
}

// Process multiple files
export const processFiles = async (files) => {
  console.log("Processing", files.length, "files")

  const results = []

  for (const file of files) {
    try {
      const result = await processFile(file)
      results.push(result)
    } catch (error) {
      console.error("Error processing file:", file.name, error)
      results.push({
        type: "error",
        content: `[Error processing file: ${file.name}]\n\nError: ${error.message}`,
        name: file.name,
        originalFile: file,
      })
    }
  }

  return results
}

// Validate file before processing
export const validateFile = (file) => {
  const maxSize = 50 * 1024 * 1024 // 50MB
  const allowedTypes = ["application/pdf", "text/plain", "text/csv", "text/markdown", "application/json"]

  if (file.size > maxSize) {
    throw new Error(`File too large: ${Math.round(file.size / 1024 / 1024)}MB. Maximum size is 50MB.`)
  }

  // Allow files based on extension as well as MIME type
  const allowedExtensions = [".pdf", ".txt", ".csv", ".md", ".json"]
  const hasAllowedExtension = allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))

  if (!allowedTypes.includes(file.type) && !hasAllowedExtension) {
    console.warn(`File type ${file.type} not in allowed list, but checking extension...`)
    if (!hasAllowedExtension) {
      throw new Error(`Unsupported file type: ${file.type}. Supported types: PDF, TXT, CSV, MD, JSON`)
    }
  }

  return true
}

// Get file info without processing content
export const getFileInfo = (file) => {
  return {
    name: file.name,
    type: file.type,
    size: file.size,
    sizeFormatted: `${Math.round(file.size / 1024)} KB`,
    lastModified: file.lastModified ? new Date(file.lastModified).toISOString() : null,
  }
}
